import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import {
  ReactCompareSlider,
  ReactCompareSliderHandle,
} from "react-compare-slider";
import { ArrowLeft, ArrowUpRight, Loader2, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Logo from "@/components/Logo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { goToSection } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const imgSrc = (u) =>
  !u || u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}/api/files/${u}`;

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProject(null);
    setNotFound(false);
    axios
      .get(`${API}/projects/${id}`)
      .then((r) => setProject(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 text-center px-6">
        <h1 className="font-serif text-4xl text-[#f5f5f0]">Project not found</h1>
        <Link
          to="/"
          data-testid="project-notfound-back"
          className="bg-[#c5832b] hover:bg-[#d9953c] text-[#121212] font-semibold px-8 py-4 rounded-[2px] transition-colors duration-300"
        >
          Back to website
        </Link>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#c5832b]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-28 pb-0">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <Link
            to="/"
            data-testid="project-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#a3a3a3] hover:text-[#c5832b] transition-colors duration-300"
          >
            <ArrowLeft size={16} /> All projects
          </Link>

          <Reveal className="mt-8">
            <Eyebrow className="mb-4">
              {project.category} · {project.year}
            </Eyebrow>
            <h1
              data-testid="project-detail-title"
              className="font-serif text-4xl md:text-6xl text-[#f5f5f0] leading-tight"
            >
              {project.title}
            </h1>
            {project.location && (
              <p className="mt-4 flex items-center gap-2 text-[#a3a3a3]">
                <MapPin size={15} className="text-[#c5832b]" />
                {project.location}
              </p>
            )}
          </Reveal>

          <Reveal className="mt-10" y={40}>
            <div
              data-testid="project-detail-slider"
              className="relative h-[420px] md:h-[680px] border border-white/10 rounded-[2px] overflow-hidden"
            >
              <ReactCompareSlider
                itemOne={
                  <img
                    src={imgSrc(project.before_url)}
                    alt={`${project.title} — before`}
                    className="w-full h-full object-cover img-treated"
                  />
                }
                itemTwo={
                  <img
                    src={imgSrc(project.after_url)}
                    alt={`${project.title} — after`}
                    className="w-full h-full object-cover img-treated"
                  />
                }
                handle={
                  <ReactCompareSliderHandle
                    buttonStyle={{
                      background: "#c5832b",
                      border: "none",
                      boxShadow: "0 0 20px rgba(197,131,43,0.45)",
                    }}
                    linesStyle={{ background: "#c5832b", width: 2 }}
                  />
                }
                style={{ width: "100%", height: "100%" }}
              />
              <span className="absolute top-4 left-4 z-10 text-[10px] uppercase tracking-[0.2em] font-semibold bg-black/60 backdrop-blur px-3 py-1.5 text-white/80 rounded-[2px]">
                Before
              </span>
              <span className="absolute top-4 right-4 z-10 text-[10px] uppercase tracking-[0.2em] font-semibold bg-[#c5832b] px-3 py-1.5 text-[#121212] rounded-[2px]">
                After
              </span>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-14">
            <Reveal>
              <Eyebrow className="mb-3">The project</Eyebrow>
              <p className="text-[#a3a3a3] text-base md:text-lg leading-relaxed">
                {project.description || "A full transformation completed by the Preece team."}
              </p>
            </Reveal>
            {project.details && (
              <Reveal delay={0.1}>
                <Eyebrow className="mb-3">Job details</Eyebrow>
                <p
                  data-testid="project-detail-details"
                  className="text-[#a3a3a3] text-base md:text-lg leading-relaxed whitespace-pre-line"
                >
                  {project.details}
                </p>
              </Reveal>
            )}
          </div>

          {project.gallery?.length > 0 && (
            <section className="mt-16">
              <Reveal>
                <Eyebrow className="mb-6">More from this job</Eyebrow>
              </Reveal>
              <div
                data-testid="project-detail-gallery"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {project.gallery.map((g, i) => (
                  <Reveal key={g + i} delay={(i % 3) * 0.08}>
                    <div className="aspect-[4/3] overflow-hidden rounded-[2px] border border-white/10 group">
                      <img
                        data-testid={`project-gallery-img-${i}`}
                        src={imgSrc(g)}
                        alt={`${project.title} photo ${i + 1}`}
                        loading="lazy"
                        className="w-full h-full object-cover img-treated transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  </Reveal>
                ))}
              </div>
            </section>
          )}

          <Reveal className="mt-20">
            <div className="bg-[#1a1a1a] border border-white/10 rounded-[2px] p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="font-serif text-3xl md:text-4xl text-[#f5f5f0]">
                  Want results <em className="italic text-[#c5832b]">like this?</em>
                </h2>
                <p className="mt-2 text-[#a3a3a3]">
                  Free, no-obligation quote — usually within the week.
                </p>
              </div>
              <button
                data-testid="project-detail-quote-cta"
                onClick={() => goToSection("quote")}
                className="group inline-flex items-center justify-center gap-3 bg-[#c5832b] hover:bg-[#d9953c] text-[#121212] font-semibold px-8 py-4 rounded-[2px] transition-colors duration-300 shrink-0"
              >
                Request a quote
                <ArrowUpRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </button>
            </div>
          </Reveal>
        </div>

        <footer className="mt-20 border-t border-white/10">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" aria-label="Back to website">
              <Logo markSize={34} />
            </Link>
            <p className="text-xs text-[#a3a3a3]/70">
              © {new Date().getFullYear()} Preece Refurbishments. Family owned & operated.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
